import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { createThreadApi, getThreads, updateThread } from "../APIs/thread";
import { useSelector } from "react-redux";
import { useEffect, useState, useContext } from "react";
import { Thread } from "../types/thread";
import ThreadList from "../components/threads/ThreadList";
import { Grid, Paper, Skeleton, Box, Button, Menu, MenuItem, ListItemText, TextField, InputAdornment, IconButton, Dialog, List, ListItemButton, ListItemIcon, Typography, Tooltip } from "@mui/material";
import ChatContainer from "../components/chat_container";
import { EditOutlined } from "@mui/icons-material";
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import { getResourcesList, ResourceItem } from "../APIs/resources";
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import WelcomeScreen from "../components/chat_container/WelcomeScreen";
import ThreadContext, { AgentMode } from "../Contexts/ThreadContext";
import { useLang } from "../Contexts/LanguageContext";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext } from "../Contexts/ThemeContext";
const ResourcesPage = () => {
    const { t, isRTL, lang, toggleLang } = useLang();
    const theme = useTheme();
    const colorMode = useContext(ColorModeContext);
    // Sidebar collapse (icon-only rail) — see the collapse toggle in the brand row.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    // Mobile: the sidebar becomes an off-canvas drawer toggled by a hamburger.
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const thread_id = searchParams.get("thread_id");
    const [threads, setThreads] = useState<Thread[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const location = useLocation();

    // This will be true if you navigated with state: { new_chat: true }

    const { userId } = useSelector((state: any) => state.auth);

    const fetchThreads = (user: string) => {
        setLoading(true);
        getThreads(user)
            .then(data => {
                const transformedThreads = data.threads.map((thread: any) => ({
                    ...thread,
                    configurable: {
                        ...thread.configurable,
                        thread_id: `${thread.configurable.thread_id[0]}_${thread.configurable.thread_id[1]}`
                    }
                }));
                setThreads(transformedThreads);
            })
            .catch(error => {
                console.error('Error fetching threads:', error);
            })
            .finally(() => setLoading(false));
    };

    
    const handleRename = async (menuThreadId: string, newName: string) => {
        let response = await updateThread(menuThreadId, newName.trim());
        if (response.status === "success") 
            fetchThreads(userId);
    };

    const handleDelete = async (menuThreadId: string) => {
        try {
            await updateThread(menuThreadId, undefined, true);
        } catch (error) {
            console.error('Delete failed:', error);
        }
        // Always re-fetch so the UI stays in sync with the server regardless of
        // whether the request succeeded or the response was blocked (e.g. CORS).
        fetchThreads(userId);
        if (menuThreadId === thread_id) {
            searchParams.delete("thread_id");
            setSearchParams(searchParams, { replace: true });
        }
    };


    useEffect(() => {
        if (userId) {
            fetchThreads(userId);
        }
    }, [userId]);

    const [threadId, setThreadId] = useState<string | null>(null);
    const [isNewChat, setIsNewChat] = useState(location.state?.new_chat || false);
    const [threaddbId, setThreaddbId] = useState<string | null>(null);
    const [agentMode, setAgentMode] = useState<AgentMode>('all');
    // Anchor for the agent-selection popup menu (null = closed).
    const [agentMenuAnchor, setAgentMenuAnchor] = useState<null | HTMLElement>(null);
    // Inline thread search: the button expands into a text input that filters the list.
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Single-source mode: the report the chat is scoped to (null = whole KB).
    // Selectable both from <SourceSelector> above the chat input and from the
    // sidebar "Report" button below; both write this shared state, which flows
    // to the chat through ThreadContext.
    const [sourceId, setSourceId] = useState<string | null>(null);
    const [sourceTitle, setSourceTitle] = useState<string | null>(null);

    // Sidebar report picker (dialog) state + the report catalog it lists.
    const [reportPickerOpen, setReportPickerOpen] = useState(false);
    const [reportQuery, setReportQuery] = useState('');
    const [resources, setResources] = useState<ResourceItem[]>([]);
    useEffect(() => {
        getResourcesList().then(setResources).catch(() => setResources([]));
    }, []);

    // Close the mobile drawer whenever the active chat changes (picking a recent
    // chat or starting a new one) so it doesn't stay covering the conversation.
    useEffect(() => {
        setMobileOpen(false);
    }, [thread_id]);

    const resourceLabel = (r: ResourceItem) =>
        ((isRTL ? (r.title_ar || r.title_en) : (r.title_en || r.title_ar)) || r.resource_id) as string;
    const filteredResources = resources.filter(r =>
        resourceLabel(r).toLowerCase().includes(reportQuery.trim().toLowerCase()));
    const closeReportPicker = () => { setReportPickerOpen(false); setReportQuery(''); };
    const selectReport = (r: ResourceItem | null) => {
        setSourceId(r ? r.resource_id : null);
        setSourceTitle(r ? (r.title_en || r.title_ar || null) : null);
        closeReportPicker();
    };

    const createThread = async () => {
        // Kept for compatibility — currently unused. Thread creation now happens
        // lazily inside ChatContainer when the user sends their first message.
        try {
            setLoading(true);

            const response = await createThreadApi();

            if (response?.thread?._id) {
                searchParams.set("thread_id", response.thread._id);
                setSearchParams(searchParams, { replace: true });
                setIsNewChat(true)
                setThreadId(response.thread.configurable.thread_id[1]);
                setThreaddbId(response.thread._id);
                response.thread.configurable.thread_name = "New Chat"
                setThreads(prevThreads => [response.thread, ...prevThreads]);
            } else {
                console.error("Thread creation failed: No thread ID returned");
            }
        } catch (error) {
            console.error("Error creating thread:", error);
        } finally {
            setLoading(false);
        }
    };

    // Enter draft mode — the actual thread is not created until the user types
    // and sends the first message inside ChatContainer.
    const handleNewChat = () => {
        searchParams.delete("thread_id");
        setSearchParams(searchParams, { replace: true });
        setThreadId(null);
        setThreaddbId(null);
        setIsNewChat(true);
    }

    // Called by ChatContainer after auto-renaming the thread on first response.
    const handleThreadRenamed = (dbId: string, newName: string) => {
        setThreads(prev => prev.map(t =>
            t._id === dbId
                ? { ...t, configurable: { ...t.configurable, thread_name: newName } }
                : t
        ));
    };

    // Called by ChatContainer once it has lazily created the underlying thread.
    const handleThreadCreated = (thread: any) => {
        thread.configurable = {
            ...thread.configurable,
            thread_name: thread.configurable?.thread_name || "New Chat",
        };
        setThreads(prevThreads => [thread, ...prevThreads]);
        setIsNewChat(false);
    }



    return (
        <ThreadContext.Provider value={{
            threadId, setThreadId, isNewChat, setIsNewChat, threaddbId, setThreaddbId,
            agentMode, setAgentMode,
            sourceId, setSourceId, sourceTitle, setSourceTitle,
            onThreadCreated: handleThreadCreated,
            onThreadRenamed: handleThreadRenamed,
        }}>
            <Paper elevation={0} square style={{ margin: 0, padding: 0, background: "transparent", height: '100%', overflow: 'hidden' }}>
                <Grid container wrap="nowrap" sx={{ height: '100%', flexWrap: 'nowrap' }}>
                    <Grid
                        sx={{
                            display: 'flex', flexDirection: 'column',
                            height: '100%', background: 'linear-gradient(180deg, #003049 0%, #00223a 100%)', borderRadius: 0, overflow: 'hidden',
                            borderRight: isRTL ? 0 : 1, borderLeft: isRTL ? 1 : 0, borderColor: 'rgba(0,0,0,0.25)',
                            paddingX: sidebarCollapsed ? 0.75 : 1, paddingY: 1.75,
                            transition: 'transform .25s ease, flex-basis .2s ease, width .2s ease, max-width .2s ease',
                            // Desktop: in-flow column (collapse-aware). Mobile: fixed off-canvas drawer.
                            // max-width must animate in step with width — on the xs breakpoint it stays
                            // a fixed 85vw regardless of collapse, so it never fights the transition there;
                            // on md, if max-width snapped to the new value instantly (unanimated) it would
                            // clamp the box to it immediately on collapse, before width's own transition
                            // could play, making collapse (shrinking into a smaller max-width) snap while
                            // expand (growing into a larger max-width, so nothing clamps) stayed smooth.
                            flex: { xs: '0 0 auto', md: `0 0 ${sidebarCollapsed ? 72 : 288}px` },
                            width: { xs: 286, md: sidebarCollapsed ? 72 : 288 },
                            maxWidth: { xs: '85vw', md: sidebarCollapsed ? 72 : 288 },
                            position: { xs: 'fixed', md: 'static' },
                            top: 0, left: 0,
                            zIndex: { xs: 1300, md: 'auto' },
                            transform: { xs: mobileOpen ? 'translateX(0)' : 'translateX(-100%)', md: 'none' },
                            boxShadow: { xs: mobileOpen ? '0 0 40px rgba(0,0,0,0.45)' : 'none', md: 'none' },
                        }}
                    >
                        {/* Brand + collapse toggle (replaces the old top navbar) */}
                        <Box sx={{ display: 'flex', flexDirection: sidebarCollapsed ? 'column' : 'row', alignItems: 'center', gap: sidebarCollapsed ? 1 : 1.25, paddingX: sidebarCollapsed ? 0 : 0.5, paddingBottom: 1.25, transition: 'gap .2s ease, padding .2s ease' }}>
                            <Box component="img" src="/enmaeya-logo.png" alt="Enmaeya" sx={{ width: 30, height: 30, objectFit: 'contain', flex: '0 0 auto' }} />
                            <Box
                                sx={{
                                    flex: 1,
                                    fontWeight: 700,
                                    color: '#fff',
                                    fontSize: '16px',
                                    letterSpacing: '.01em',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    opacity: sidebarCollapsed ? 0 : 1,
                                    maxWidth: sidebarCollapsed ? 0 : 160,
                                    transition: 'opacity .2s ease, max-width .2s ease',
                                }}
                            >
                                enmaeya
                            </Box>
                            <IconButton
                                onClick={() => { if (mobileOpen) setMobileOpen(false); else setSidebarCollapsed(v => !v); }}
                                size="small"
                                title={mobileOpen ? 'Close menu' : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')}
                                sx={{ color: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
                            >
                                {mobileOpen
                                    ? <CloseRoundedIcon fontSize="small" />
                                    : (sidebarCollapsed ? <KeyboardDoubleArrowRightRoundedIcon fontSize="small" /> : <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />)}
                            </IconButton>
                        </Box>
                        {(() => {
                            // Shared "sidebar row" style so New Chat, Search, and the
                            // Agent selector read as one consistent control set (ChatGPT/
                            // Claude style): 8px radius, equal height/padding, muted icons,
                            // transparent with a subtle hover on the dark rail.
                            const rowSx = {
                                justifyContent: 'flex-start',
                                gap: 1,
                                minHeight: 42,
                                paddingX: 1.5,
                                paddingY: 1,
                                borderRadius: '8px',
                                textTransform: 'none',
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
                                transition: 'padding .2s ease',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', boxShadow: 'none' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                            } as const;

                            // Inline search field — same dimensions as the row buttons so the
                            // button→input swap is seamless (8px radius, 42px tall, 12px inset).
                            const searchFieldSx = {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    minHeight: 42,
                                    paddingInlineStart: '12px',
                                    backgroundColor: 'rgba(255,255,255,0.06)',
                                    color: 'rgba(255,255,255,0.9)',
                                    fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
                                    fontSize: '0.9rem',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
                                },
                                '& .MuiInputAdornment-root': { marginInlineEnd: '8px' },
                                '& input': { color: 'rgba(255,255,255,0.9)', '&::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } },
                            } as const;

                            // Leading icon + label, shared by New Chat and the Agent row so
                            // both icons sit at the same x as the search icon (12px inset).
                            // The label stays mounted and fades/narrows via CSS transition
                            // (instead of being added/removed) so collapse and expand read
                            // as the same animation played in reverse.
                            const rowLeading = (icon: React.ReactNode, label: string) => (
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', minWidth: 0, justifyContent: 'center' }}>
                                    {icon}
                                    <Box
                                        component="span"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            opacity: sidebarCollapsed ? 0 : 1,
                                            maxWidth: sidebarCollapsed ? 0 : 220,
                                            marginInlineStart: sidebarCollapsed ? 0 : 1,
                                            transition: 'opacity .2s ease, max-width .2s ease, margin-inline-start .2s ease',
                                        }}
                                    >
                                        {label}
                                    </Box>
                                </Box>
                            );
                            const collapsedRowSx = sidebarCollapsed ? { justifyContent: 'center', paddingX: 0, minWidth: 0 } : {};
                            // Icon-only rows lose their visible label, so show it as a
                            // tooltip instead — with no open delay, since it's replacing
                            // information that's normally just there to read, not a
                            // secondary hint that can afford to wait.
                            const collapsedTooltipProps = (label: string) => ({
                                title: label,
                                disableHoverListener: !sidebarCollapsed,
                                disableFocusListener: !sidebarCollapsed,
                                enterDelay: 0,
                                enterNextDelay: 0,
                                placement: 'right' as const,
                                arrow: true,
                                slotProps: { transition: { timeout: 0 } },
                            });

                            const agentOptions: { value: AgentMode; label: string }[] = [
                                { value: 'all', label: t.allAgents },
                                { value: 'environment', label: t.environment },
                                { value: 'health', label: t.health },
                                { value: 'education', label: t.education },
                            ];
                            const currentLabel = agentOptions.find(o => o.value === agentMode)?.label ?? t.allAgents;

                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, paddingX: sidebarCollapsed ? 0 : 0.5, marginBottom: 1 }}>
                                    {/* New chat */}
                                    <Tooltip {...collapsedTooltipProps(t.newChat)}>
                                        {/* Wrapped in a span: Tooltip needs hover/focus events on its
                                            child, and a disabled Button doesn't reliably fire them. */}
                                        <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                            <Button
                                                fullWidth
                                                disabled={isNewChat}
                                                onClick={handleNewChat}
                                                sx={{
                                                    ...rowSx,
                                                    ...collapsedRowSx,
                                                    // Primary action: keep a faint persistent highlight (matches the mockup).
                                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                                    fontWeight: 600,
                                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)', boxShadow: 'none' },
                                                }}
                                            >
                                                {rowLeading(
                                                    <AddRoundedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />,
                                                    t.newChat,
                                                )}
                                            </Button>
                                        </Box>
                                    </Tooltip>

                                    {/* Agent selector (opens a popup) */}
                                    <Tooltip {...collapsedTooltipProps(currentLabel)}>
                                        <Box component="span" sx={{ display: 'block', width: '100%' }}>
                                            <Button
                                                fullWidth
                                                disabled={!!sourceId}
                                                onClick={(e) => setAgentMenuAnchor(e.currentTarget)}
                                                endIcon={sidebarCollapsed ? undefined : <ArrowDropDownRoundedIcon />}
                                                sx={{ ...rowSx, justifyContent: sidebarCollapsed ? 'center' : 'space-between', paddingX: sidebarCollapsed ? 0 : 1.5, minWidth: sidebarCollapsed ? 0 : undefined, '& .MuiButton-endIcon': { color: 'rgba(255,255,255,0.7)' } }}
                                            >
                                                {rowLeading(
                                                    <GridViewOutlinedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />,
                                                    currentLabel,
                                                )}
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={agentMenuAnchor}
                                        open={Boolean(agentMenuAnchor)}
                                        onClose={() => setAgentMenuAnchor(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
                                        slotProps={{ paper: { dir: isRTL ? 'rtl' : 'ltr', sx: { minWidth: 220, borderRadius: 2 } } }}
                                    >
                                        {agentOptions.map(o => (
                                            <MenuItem
                                                key={o.value}
                                                selected={o.value === agentMode}
                                                onClick={() => { setAgentMode(o.value); setAgentMenuAnchor(null); }}
                                                sx={{
                                                    fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
                                                    '&.Mui-selected': { backgroundColor: 'rgba(78,201,176,0.16)', '&:hover': { backgroundColor: 'rgba(78,201,176,0.24)' } },
                                                }}
                                            >
                                                <ListItemText primary={o.label} />
                                                {o.value === agentMode && <CheckRoundedIcon fontSize="small" sx={{ color: '#3db8a0', marginInlineStart: 1 }} />}
                                            </MenuItem>
                                        ))}
                                        {/* Economics agent: not built yet, shown disabled with a "coming soon" hint. */}
                                        <MenuItem
                                            disabled
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit',
                                            }}
                                        >
                                            <ListItemText primary={t.economics} />
                                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                                {t.comingSoon}
                                            </Typography>
                                        </MenuItem>
                                    </Menu>

                                    {/* Report selection (opens a picker dialog) */}
                                    <Tooltip {...collapsedTooltipProps(sourceTitle || t.selectReport)}>
                                        <Button
                                            fullWidth
                                            onClick={() => setReportPickerOpen(true)}
                                            sx={{
                                                ...rowSx,
                                                ...collapsedRowSx,
                                                ...(sourceId ? { backgroundColor: 'rgba(78,201,176,0.16)', '&:hover': { backgroundColor: 'rgba(78,201,176,0.24)', boxShadow: 'none' } } : {}),
                                            }}
                                        >
                                            {rowLeading(
                                                <DescriptionOutlinedIcon fontSize="small" sx={{ color: sourceId ? '#3db8a0' : 'rgba(255,255,255,0.7)' }} />,
                                                sourceTitle || t.selectReport,
                                            )}
                                        </Button>
                                    </Tooltip>

                                    {/* Search — a button that expands into an inline filter input */}
                                    {searchOpen ? (
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            size="small"
                                            placeholder={t.search}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); setSearchOpen(false); } }}
                                            onBlur={() => { if (!searchQuery.trim()) setSearchOpen(false); }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchRoundedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchOpen(false); }} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                                            <CloseRoundedIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={searchFieldSx}
                                        />
                                    ) : (
                                        <Tooltip {...collapsedTooltipProps(t.search)}>
                                            <Button
                                                fullWidth
                                                onClick={() => sidebarCollapsed ? setSidebarCollapsed(false) : setSearchOpen(true)}
                                                sx={{ ...rowSx, ...collapsedRowSx }}
                                            >
                                                {rowLeading(
                                                    <SearchOutlinedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />,
                                                    t.search,
                                                )}
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })()}
                        {/* Recent chats: stays mounted at a constant flex size and just
                            fades with the width transition, so the footer below never
                            jumps and collapse mirrors expand exactly. */}
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
                            opacity: sidebarCollapsed ? 0 : 1,
                            transition: 'opacity .2s ease',
                            pointerEvents: sidebarCollapsed ? 'none' : 'auto',
                        }}>
                            {/* Divider separating the action buttons from the chat history. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingX: 1.5, marginTop: 0.5, marginBottom: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: isRTL ? 0 : 1, whiteSpace: 'nowrap', fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit' }}
                                >
                                    {t.recent}
                                </Typography>
                                <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.12)' }} />
                            </Box>
                            <Box dir="ltr" sx={{ flex: 1, overflowY: 'auto', minHeight: 0, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                                {loading ? (
                                    <Box>
                                        {[...Array(5)].map((_, i) => (
                                            <Skeleton key={i} variant="rectangular" height={40} sx={{ marginBottom: 1 }} />
                                        ))}
                                    </Box>
                                ) : (
                                    <ThreadList
                                        threads={searchQuery.trim()
                                            ? threads.filter(th => (th.configurable?.thread_name || '')
                                                .toLowerCase()
                                                .includes(searchQuery.trim().toLowerCase()))
                                            : threads}
                                        onRename={handleRename}
                                        onDelete={handleDelete}
                                    />
                                )}
                            </Box>
                        </Box>
                        {/* Footer: language + theme toggles (relocated from the old navbar) */}
                        <Box sx={{ marginTop: 'auto', paddingTop: 1.25, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 1, flexDirection: sidebarCollapsed ? 'column' : 'row', alignItems: 'stretch' }}>
                            <Button
                                onClick={toggleLang}
                                sx={{ flex: 1, minWidth: 0, color: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, paddingY: 0.75, fontFamily: lang === 'ar' ? "'Noto Kufi Arabic', sans-serif" : 'inherit', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
                            >
                                {sidebarCollapsed ? (lang === 'en' ? 'ع' : 'EN') : (lang === 'en' ? 'العربية' : 'English')}
                            </Button>
                            <IconButton
                                onClick={colorMode.toggleColorMode}
                                title="Toggle theme"
                                sx={{ color: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2 }}
                            >
                                {theme.palette.mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                            </IconButton>
                        </Box>
                    </Grid>
                    <Grid sx={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden', backgroundColor: (t2) => t2.palette.mode === 'dark' ? '#1b1c1d' : '#ffffff' }}>
                        <ChatContainer />
                    </Grid>
                </Grid>
            </Paper>

            {/* Mobile: hamburger to open the sidebar drawer (hidden on desktop and
                while the drawer is open) + a backdrop that closes it. */}
            <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                sx={{ display: { xs: mobileOpen ? 'none' : 'inline-flex', md: 'none' }, position: 'fixed', top: 8, insetInlineStart: 8, zIndex: 1200, color: '#00314f', backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: 3, '&:hover': { backgroundColor: '#fff' } }}
            >
                <MenuRoundedIcon />
            </IconButton>
            <Box
                onClick={() => setMobileOpen(false)}
                sx={{ display: { xs: mobileOpen ? 'block' : 'none', md: 'none' }, position: 'fixed', inset: 0, zIndex: 1299, backgroundColor: 'rgba(0,0,0,0.45)' }}
            />

            {/* Report picker dialog, opened by the sidebar "Report" button. */}
            <Dialog
                open={reportPickerOpen}
                onClose={closeReportPicker}
                slotProps={{ paper: { dir: isRTL ? 'rtl' : 'ltr', sx: { width: 'fit-content', minWidth: 'min(420px, 90vw)', maxWidth: 'min(720px, 92vw)', borderRadius: '20px' } } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 6px' }}>
                    <Typography variant="subtitle1" sx={{ color: '#00314f', fontWeight: 700, fontFamily: isRTL ? "'Noto Kufi Arabic', sans-serif" : 'inherit' }}>
                        {t.source}
                    </Typography>
                    <IconButton size="small" onClick={closeReportPicker}>
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ padding: '0 18px 18px' }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder={t.sourcePlaceholder}
                        value={reportQuery}
                        onChange={(e) => setReportQuery(e.target.value)}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment>) }}
                        sx={{ marginBottom: 1 }}
                    />
                    <List dense sx={{ maxHeight: 320, overflowY: 'auto', padding: 0 }}>
                        {sourceId && (
                            <ListItemButton onClick={() => selectReport(null)}>
                                <ListItemIcon sx={{ minWidth: 36 }}><CloseRoundedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={t.clearSource} />
                            </ListItemButton>
                        )}
                        {filteredResources.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', padding: 2, textAlign: 'center' }}>
                                {t.noReports}
                            </Typography>
                        ) : (
                            filteredResources.map((r) => (
                                <ListItemButton
                                    key={r.resource_id}
                                    selected={r.resource_id === sourceId}
                                    onClick={() => selectReport(r)}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}><DescriptionRoundedIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary={resourceLabel(r)} primaryTypographyProps={{ noWrap: true }} />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </Box>
            </Dialog>
        </ThreadContext.Provider>
    );
};

export default ResourcesPage;
